import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientsApi, permissionsApi } from '../../../api/client';

/**
 * TenantPicker — shared dropdown to filter ACD views by client tenant.
 *
 * Visibility rules:
 *   - Admin / Continuum employees: dropdown shows "All tenants" + every client.
 *   - Tenant-bound users: dropdown is hidden (their view is auto-scoped to
 *     their own tenant server-side).
 *
 * Stores selection in localStorage so the choice persists across reloads
 * and across the four ACD tabs.
 */
const STORAGE_KEY = 'cx.acd.tenantFilter';

export function useTenantFilter() {
  const [clientId, setClientId] = React.useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; }
  });
  const apply = React.useCallback((v) => {
    setClientId(v);
    try {
      if (v) localStorage.setItem(STORAGE_KEY, v);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  }, []);
  return [clientId, apply];
}

export default function TenantPicker({ value, onChange }) {
  const { data: branding } = useQuery({
    queryKey: ['branding', 'me'],
    queryFn: () => permissionsApi.branding(),
    staleTime: 60_000,
  });
  // Only admins/Continuum employees can pick. Tenant-bound users see nothing.
  const isContinuum = !!branding?.is_continuum_employee;

  const { data: clients = [] } = useQuery({
    queryKey: ['admin', 'clients-light'],
    queryFn: () => clientsApi.list(),
    enabled: isContinuum,
    staleTime: 60_000,
  });

  if (!isContinuum) return null;

  return (
    <div className="flex items-center gap-2 text-xs">
      <label className="text-surface-muted whitespace-nowrap">Client</label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin"
      >
        <option value="">All tenants</option>
        {clients.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </div>
  );
}
